import { API_REVENUE_SOURCE_PERFORMANCE_EXPORT } from "../../../../utils/apis";
import { NoWrap, numberFormat, returnObject } from "../../../../utils/helpers";

type TRevenueSourcePerformanceProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useRevenueSourcePerformance = ({
  dateFilterKey,
  noWrapColumns = [],
  numberColumns = [],
}: TRevenueSourcePerformanceProps) => {
  const tableColumns = () => {
    // Custom columns for revenue source data with specific filters
    const filterColumns = [
      {
        id: "revenue_source",
        header: <NoWrap>Revenue Source</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "registrations",
        header: <NoWrap>Registrations</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "assessments",
        header: <NoWrap>Assessments</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "payments",
        header: <NoWrap>Payments</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "outstanding_balance",
        header: <NoWrap>Outstanding Balance</NoWrap>,
        filter: { type: "number" },
      },
    ];

    // return [...filterColumns, ...columns];
    return [...filterColumns];
  };

  const tableData = (data: any) => {
    // Handle both data.results and direct array, and also full response object
    let dataArray;
    if (data?.results) {
      dataArray = data.results;
    } else if (Array.isArray(data)) {
      dataArray = data;
    } else {
      dataArray = [];
    }

    return dataArray?.map((item: any) => ({
      ...item,
      [dateFilterKey || "created_at"]: (
        <NoWrap>{item[dateFilterKey || "created_at"]}</NoWrap>
      ),
      revenue_source: (
        <span
          className={`capitalize font-medium px-3 py-1 rounded-full text-sm ${
            item.revenue_source === "advance"
              ? "bg-blue-100 text-blue-800"
              : item.revenue_source === "booking"
                ? "bg-green-100 text-green-800"
                : item.revenue_source === "parking"
                  ? "bg-purple-100 text-purple-800"
                  : item.revenue_source === "penalty"
                    ? "bg-red-100 text-red-800"
                    : item.revenue_source === "coupon"
                      ? "bg-orange-100 text-orange-800"
                      : "bg-gray-100 text-gray-800"
          }`}
        >
          <NoWrap>{item.revenue_source}</NoWrap>
        </span>
      ),
      registrations: (
        <span className="font-medium text-gray-900">
          {numberFormat(item.registrations)}
        </span>
      ),
      assessments: (
        <span className="font-medium text-green-600">
          {numberFormat(item.assessments)}
        </span>
      ),
      payments:
        item.payments !== null && item.payments !== undefined ? (
          <span className="font-medium text-blue-600">
            {numberFormat(item.payments)}
          </span>
        ) : (
          <span className="text-gray-400 italic">No payments</span>
        ),
      outstanding_balance:
        item.outstanding_balance !== null &&
        item.outstanding_balance !== undefined ? (
          <span
            className={`font-medium ${
              item.outstanding_balance > 0 ? "text-red-600" : "text-green-600"
            }`}
          >
            {numberFormat(item.outstanding_balance)}
          </span>
        ) : (
          <span className="text-gray-400 italic">N/A</span>
        ),
      payment_rate:
        item.assessments > 0 &&
        item.payments !== null &&
        item.payments !== undefined ? (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              (item.payments / item.assessments) * 100 >= 80
                ? "bg-green-100 text-green-800"
                : (item.payments / item.assessments) * 100 >= 50
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
            }`}
          >
            {((item.payments / item.assessments) * 100).toFixed(1)}%
          </span>
        ) : (
          <span className="text-gray-400">N/A</span>
        ),
      average_assessment_value:
        item.registrations > 0 ? (
          <span className="font-medium text-indigo-600">
            {numberFormat(Math.round(item.assessments / item.registrations))}
          </span>
        ) : (
          <span className="text-gray-400">N/A</span>
        ),
      ...returnObject(
        noWrapColumns.length > 0,
        noWrapColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = <NoWrap>{item[column]}</NoWrap>;
          }
          return obj;
        }, {}),
      ),
      ...returnObject(
        numberColumns.length > 0,
        numberColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = numberFormat(item[column]);
          }
          return obj;
        }, {}),
      ),
    }));
  };

  // Additional utility functions for revenue source analysis
  const getRevenueSourceStats = (data: any) => {
    // Handle both data.results and direct array
    const results = data?.results || data || [];

    const totalRegistrations = results.reduce(
      (sum: number, item: any) => sum + (item.registrations || 0),
      0,
    );
    const totalAssessments = results.reduce(
      (sum: number, item: any) => sum + (item.assessments || 0),
      0,
    );
    const totalPayments = results.reduce(
      (sum: number, item: any) => sum + (item.payments || 0),
      0,
    );
    const totalOutstanding = results.reduce(
      (sum: number, item: any) => sum + (item.outstanding_balance || 0),
      0,
    );

    const overallPaymentRate =
      totalAssessments > 0 ? (totalPayments / totalAssessments) * 100 : 0;
    const averageAssessmentValue =
      totalRegistrations > 0 ? totalAssessments / totalRegistrations : 0;

    return {
      totalRegistrations,
      totalAssessments,
      totalPayments,
      totalOutstanding,
      overallPaymentRate: overallPaymentRate.toFixed(1),
      averageAssessmentValue,
      numberOfSources: results.length,
      activePaymentSources: results.filter(
        (item: any) => item.payments !== null && item.payments > 0,
      ).length,
    };
  };

  const getSourcePerformance = (data: any) => {
    // Handle both data.results and direct array
    const results = data?.results || data || [];

    const totalAssessmentsValue = results.reduce(
      (sum: number, item: any) => sum + (item.assessments || 0),
      0,
    );

    return results
      .map((item: any) => {
        const paymentRate =
          item.assessments > 0 &&
          item.payments !== null &&
          item.payments !== undefined
            ? (item.payments / item.assessments) * 100
            : 0;
        const avgAssessmentValue =
          item.registrations > 0 ? item.assessments / item.registrations : 0;
        const avgPaymentValue =
          item.registrations > 0 &&
          item.payments !== null &&
          item.payments !== undefined
            ? item.payments / item.registrations
            : 0;

        return {
          ...item,
          payment_rate: paymentRate.toFixed(1),
          average_assessment_value: avgAssessmentValue,
          average_payment_value: avgPaymentValue,
          performance_rating:
            paymentRate >= 80
              ? "Excellent"
              : paymentRate >= 60
                ? "Good"
                : paymentRate >= 40
                  ? "Fair"
                  : paymentRate > 0
                    ? "Poor"
                    : "No Payments",
          revenue_contribution:
            totalAssessmentsValue > 0
              ? ((item.assessments / totalAssessmentsValue) * 100).toFixed(1)
              : "0.0",
        };
      })
      .sort((a: any, b: any) => b.assessments - a.assessments); // Sort by assessments descending
  };

  const getPaymentAnalysis = (data: any) => {
    // Handle both data.results and direct array
    const results = data?.results || data || [];

    const withPayments = results.filter(
      (item: any) =>
        item.payments !== null &&
        item.payments !== undefined &&
        item.payments > 0,
    );
    const withoutPayments = results.filter(
      (item: any) =>
        item.payments === null ||
        item.payments === undefined ||
        item.payments === 0,
    );

    return {
      sourcesWithPayments: withPayments.length,
      sourcesWithoutPayments: withoutPayments.length,
      topPerformingSources: withPayments
        .map((item: any) => ({
          revenue_source: item.revenue_source,
          payment_rate:
            item.assessments > 0
              ? ((item.payments / item.assessments) * 100).toFixed(1)
              : "0.0",
          total_payments: item.payments,
        }))
        .sort(
          (a: any, b: any) =>
            parseFloat(b.payment_rate) - parseFloat(a.payment_rate),
        )
        .slice(0, 3),
      highestRevenueSources: results
        .map((item: any) => ({
          revenue_source: item.revenue_source,
          assessments: item.assessments,
          registrations: item.registrations,
        }))
        .sort((a: any, b: any) => b.assessments - a.assessments)
        .slice(0, 3),
    };
  };

  const exportData = {
    api: API_REVENUE_SOURCE_PERFORMANCE_EXPORT,
    fileName: `payments_${new Date().toISOString().split("T")[0]}.xlsx`,
    showCustomDateRange: true,
    start_date: "start_date",
    end_date: "end_date",
  };

  const tableTitle = "Revenue Source Performance Report";

  return {
    tableColumns,
    tableData,
    tableTitle,
    getRevenueSourceStats,
    getSourcePerformance,
    getPaymentAnalysis,
    exportData,
  };
};

export default useRevenueSourcePerformance;
